"use client";
import { Box, Button, Grid, Typography } from "@mui/material";
import FormTextFieldSimple from "../formulari/FormTextFieldSimple";
import { pxToVw } from "@/utils/pxToVw";
import { getTextWithContext, useText, useTexts } from "@/context/TextContext";
import { useActionState, useEffect, useState } from "react";
import { resetPasswordSchema } from "@/lib/schemas/login";
import z from "zod";
import FormWrapper from "../FormWrapper";
import { resetPasswordAction } from "@/app/actions";
import { useRouter } from "next/navigation";

export default function ResetPasswordForm({
  code,
  isWelcome = false,
}: {
  code?: string | Array<string>;
  isWelcome?: boolean;
}) {
  const [form, setForm] = useState({ pass: "", confirmPass: "" });
  const [errors, setErrors] = useState({} as any);
  const [state, formAction, pending] = useActionState(resetPasswordAction, {
    errors: undefined,
  });

  const context = useTexts();
  const router = useRouter();

  const validateField = (fieldName: keyof typeof form) => {
    const data = {
      ...form,
    };

    const result = resetPasswordSchema.safeParse(data);

    let fieldErrors = result.success
      ? {}
      : (z.treeifyError(result.error).properties as any);

    fieldErrors = Object.fromEntries(
      Object.entries(fieldErrors).filter(
        ([key, value]) => data[key as keyof typeof form] !== "",
      ),
    );

    setErrors(fieldErrors);
  };

  const handleChange = (value: any, fieldName: keyof typeof form) => {
    setForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  useEffect(() => {
    if (state?.errors) {
      setErrors(state.errors);
    }
    if (state?.success) {
      setTimeout(() => {
        const locale = context.locale;
        router.push((locale !== "es-es" ? `/${locale}` : "") + "/login");
      }, 6000);
    }
  }, [state, router, context.locale]);

  return (
    <Grid size={{ xs: 12, md: 5.5 }}>
      <Typography variant="body5" color="warm2" mb={pxToVw(52)}>
        {useText(
          isWelcome ? "createPassword.subtitle" : "resetPassword.subtitle",
        )}
      </Typography>
      <FormWrapper
        errors={errors}
        success={
          state?.success
            ? getTextWithContext(
                isWelcome ? "createPassword.success" : "resetPassword.success",
                context,
              )
            : undefined
        }
        action={formAction}
        pending={pending}
      >
        <input type="hidden" name="code" value={code} />
        <Box mb={pxToVw(45)}>
          <FormTextFieldSimple
            name="pass"
            type="password"
            startAdornmentText={useText("login.form.passwordLabel")}
            autoComplete="new-password"
            value={form.pass}
            onBlur={(e) => validateField("pass")}
            onChange={(e) => handleChange(e.target.value, "pass")}
            error={!!errors.pass}
            helperText={
              errors.pass
                ? getTextWithContext(errors.pass.errors[0], context)
                : ""
            }
          />
        </Box>
        <Box mb={pxToVw(53)}>
          <FormTextFieldSimple
            name="confirmPass"
            type="password"
            isStartAdornmentTooLong={false}
            startAdornmentText={useText("register.form.repeatPasswordLabel")}
            autoComplete="new-password"
            value={form.confirmPass}
            onBlur={(e) => validateField("confirmPass")}
            onChange={(e) => handleChange(e.target.value, "confirmPass")}
            error={!!errors.confirmPass}
            helperText={
              errors.confirmPass
                ? getTextWithContext(errors.confirmPass.errors[0], context)
                : ""
            }
          />
        </Box>

        <Grid container>
          <Button
            variant="botonMarronInverso"
            id="button-reset-enviar"
            type="submit"
          >
            {useText("recoverPassword.sendButton")}
          </Button>
        </Grid>
      </FormWrapper>
    </Grid>
  );
}
